<template>
  <v-form ref="form" class="store-data-form">
    <v-combobox
      v-model="city"
      :items="cityItems"
      label="Stadt"
      required
    ></v-combobox>

    <h2>Allgemein</h2>
    <v-file-input
      ref="avatarInput"
      :rules="avatarRules"
      @change="avatar = $event"
      label="Avatar (optimal 128x128px, max. 100 KB)"
      show-size
      required
    ></v-file-input>
    <v-list-item-avatar v-if="avatar" class="avatar">
      <v-img :src="avatar" />
    </v-list-item-avatar>
    <v-text-field v-model="name" label="Name" required></v-text-field>
    <v-text-field
      v-model="legalName"
      label="Rechtsgültiger Name"
      required
    ></v-text-field>
    <v-combobox
      v-model="category"
      :items="categoryItems"
      label="Kategorie"
      required
    ></v-combobox>
    <v-combobox
      v-model="labels"
      :items="labelItems"
      label="Siegel"
      multiple
      clearable
      required
    ></v-combobox>
    <br />

    <h2>Nähere Infos</h2>
    <v-file-input
      ref="logoInput"
      :rules="logoRules"
      @change="logo = $event"
      id="logo"
      label="Logo (optional, max. 500 KB)"
      show-size
    ></v-file-input>
    <v-img v-if="logo" class="logo" :src="logo" contain />
    <v-textarea
      v-model="description"
      label="Beschreibung"
      :rules="descriptionRules"
      :counter="MAX_DESCRIPTION_LENGTH"
      :maxlength="MAX_DESCRIPTION_LENGTH"
      required
    ></v-textarea>
    <v-text-field v-model="website" label="Webseite" required></v-text-field>
    <v-text-field v-model="phone" label="Telefon"></v-text-field>
    <v-text-field v-model="email" label="E-Mail"></v-text-field>
    <br />

    <h2>Standort</h2>
    <v-text-field v-model="lat" label="Breitengrad" required></v-text-field>
    <v-text-field v-model="lng" label="Längengrad" required></v-text-field>
    <v-text-field v-model="street" label="Straße" required></v-text-field>
    <v-text-field
      v-model="houseNumber"
      label="Hausnummer"
      required
    ></v-text-field>
    <v-text-field v-model="zipCode" label="PLZ" required></v-text-field>
    <v-text-field v-model="cityName" label="Ort" required></v-text-field>
    <br />

    <h2>Öffnungszeiten</h2>
    <v-switch v-model="useOpeningHoursText" label="Freitext"></v-switch>
    <section v-if="!useOpeningHoursText">
      <v-text-field
        ref="mondayInput"
        :rules="timeRules"
        v-model="monday"
        label="Montag"
        required
      ></v-text-field>
      <v-text-field
        ref="tuesdayInput"
        :rules="timeRules"
        v-model="tuesday"
        label="Dienstag"
        required
      ></v-text-field>
      <v-text-field
        ref="wednesdayInput"
        :rules="timeRules"
        v-model="wednesday"
        label="Mittwoch"
        required
      ></v-text-field>
      <v-text-field
        ref="thursdayInput"
        :rules="timeRules"
        v-model="thursday"
        label="Donnerstag"
        required
      ></v-text-field>
      <v-text-field
        ref="fridayInput"
        :rules="timeRules"
        v-model="friday"
        label="Freitag"
        required
      ></v-text-field>
      <v-text-field
        ref="saturdayInput"
        :rules="timeRules"
        v-model="saturday"
        label="Samstag"
        required
      ></v-text-field>
      <v-text-field
        ref="sundayInput"
        :rules="timeRules"
        v-model="sunday"
        label="Sonntag"
        required
      ></v-text-field>
    </section>
    <section v-else>
      <v-text-field
        v-model="openingHoursText"
        label="Freitext"
        required
      ></v-text-field>
    </section>
  </v-form>
</template>

<style lang="scss" scoped>
.avatar {
  display: block;
  margin: 0 auto;
}

.logo {
  display: block;
  margin-bottom: 1.5rem;
  max-width: 100%;
  max-height: 5rem;
}
</style>

<script>
export default {
  name: "StoreDataForm",

  data: function() {
    return {
      MAX_DESCRIPTION_LENGTH: 600,

      avatarRules: [
        value =>
          !value ||
          value.size < 100000 ||
          "Der Avatar darf maximal 100 KB groß sein!"
      ],
      logoRules: [
        value =>
          !value ||
          value.size < 500000 ||
          "Das Logo darf maximal 500 KB groß sein!"
      ],
      descriptionRules: [
        value =>
          value.length <= this.MAX_DESCRIPTION_LENGTH ||
          `Die Beschreibung darf maximal ${this.MAX_DESCRIPTION_LENGTH} Zeichen lang sein`
      ],
      timeRules: [
        value =>
          !value ||
          value.match(
            /^(\d{2}:\d{2}-\d{2}:\d{2},){0,}(\d{2}:\d{2}-\d{2}:\d{2})$/
          ) != null
      ],

      customOpeningHoursText: false
    };
  },

  computed: {
    cityItems: function() {
      let cities = [];

      for (let city of this.$store.getters["cities/all"]()) {
        cities.push({
          text: `${city.name} | ${city.location.federalState}`,
          value: city._id
        });
      }

      return cities;
    },

    categoryItems: function() {
      let categories = [];

      for (let category of this.$store.getters["categories/all"]()) {
        categories.push({
          text: category.name,
          value: category._id
        });
      }

      return categories;
    },

    labelItems: function() {
      let labels = [];

      for (let label of this.$store.state.labels.all) {
        labels.push({
          text: label.name,
          value: label._id
        });
      }

      return labels;
    },

    city: {
      get() {
        let item = this.cityItems.find(
          v => v.value === this.$store.state.storeEditor.data.cityId
        );
        return item === undefined ? null : item;
      },
      set(item) {
        this.$store.commit(
          "storeEditor/setCity",
          item == null ? null : item.value
        );
      }
    },

    avatar: {
      get() {
        return this.$store.state.storeEditor.data.avatar;
      },
      set(imageFile) {
        this.$refs.form.validate();
        if (this.$refs.avatarInput.valid === true) {
          this.$parseImage(imageFile).then(result =>
            this.$store.commit("storeEditor/setAvatar", result)
          );
        }
      }
    },
    name: {
      get() {
        return this.$store.state.storeEditor.data.name;
      },
      set(value) {
        this.$store.commit("storeEditor/setName", value);
      }
    },
    legalName: {
      get() {
        return this.$store.state.storeEditor.data.legalName;
      },
      set(value) {
        this.$store.commit("storeEditor/setLegalName", value);
      }
    },
    category: {
      get() {
        let item = this.categoryItems.find(
          v => v.value === this.$store.state.storeEditor.data.categoryId
        );
        return item === undefined ? null : item;
      },
      set(item) {
        this.$store.commit(
          "storeEditor/setCategory",
          item == null ? null : item.value
        );
      }
    },
    labels: {
      get() {
        return this.labelItems.filter(v =>
          this.$store.state.storeEditor.data.labelIds.includes(v.value)
        );
      },
      set(items) {
        this.$store.commit(
          "storeEditor/setLabels",
          items.map(v => v.value)
        );
      }
    },

    logo: {
      get() {
        return this.$store.state.storeEditor.data.logo;
      },
      set(imageFile) {
        this.$refs.form.validate();
        if (this.$refs.logoInput.valid === true) {
          this.$parseImage(imageFile).then(result =>
            this.$store.commit("storeEditor/setLogo", result)
          );
        }
      }
    },
    description: {
      get() {
        return this.$store.state.storeEditor.data.description;
      },
      set(value) {
        this.$store.commit("storeEditor/setDescription", value);
      }
    },
    website: {
      get() {
        return this.$store.state.storeEditor.data.website;
      },
      set(value) {
        this.$store.commit("storeEditor/setWebsite", value);
      }
    },
    phone: {
      get() {
        return this.$store.state.storeEditor.data.phone;
      },
      set(value) {
        this.$store.commit("storeEditor/setPhone", value);
      }
    },
    email: {
      get() {
        return this.$store.state.storeEditor.data.email;
      },
      set(value) {
        this.$store.commit("storeEditor/setEmail", value);
      }
    },

    lat: {
      get() {
        return this.$store.state.storeEditor.data.location.lat;
      },
      set(value) {
        this.$store.commit("storeEditor/setLat", value);
      }
    },
    lng: {
      get() {
        return this.$store.state.storeEditor.data.location.lng;
      },
      set(value) {
        this.$store.commit("storeEditor/setLng", value);
      }
    },
    street: {
      get() {
        return this.$store.state.storeEditor.data.location.street;
      },
      set(value) {
        this.$store.commit("storeEditor/setStreet", value);
      }
    },
    houseNumber: {
      get() {
        return this.$store.state.storeEditor.data.location.houseNumber;
      },
      set(value) {
        this.$store.commit("storeEditor/setHouseNumber", value);
      }
    },
    zipCode: {
      get() {
        return this.$store.state.storeEditor.data.location.zipCode;
      },
      set(value) {
        this.$store.commit("storeEditor/setZipCode", value);
      }
    },
    cityName: {
      get() {
        return this.$store.state.storeEditor.data.location.city;
      },
      set(value) {
        this.$store.commit("storeEditor/setCityName", value);
      }
    },

    useOpeningHoursText: {
      get() {
        return this.$store.state.storeEditor.data.useOpeningHoursText;
      },
      set(value) {
        this.$store.commit("storeEditor/setUseOpeningHoursText", value);
      }
    },
    openingHoursText: {
      get() {
        return this.$store.state.storeEditor.data.openingHoursText;
      },
      set(value) {
        this.$store.commit("storeEditor/setOpeningHoursText", value);
      }
    },

    monday: {
      get() {
        return this.parseTimes(
          this.$store.state.storeEditor.data.openingHours.monday
        );
      },
      set(value) {
        this.$refs.form.validate();
        if (this.$refs.mondayInput.valid === true) {
          this.$store.commit("storeEditor/setMonday", this.parseTimeStr(value));
        }
      }
    },
    tuesday: {
      get() {
        return this.parseTimes(
          this.$store.state.storeEditor.data.openingHours.tuesday
        );
      },
      set(value) {
        this.$refs.form.validate();
        if (this.$refs.tuesdayInput.valid === true) {
          this.$store.commit(
            "storeEditor/setTuesday",
            this.parseTimeStr(value)
          );
        }
      }
    },
    wednesday: {
      get() {
        return this.parseTimes(
          this.$store.state.storeEditor.data.openingHours.wednesday
        );
      },
      set(value) {
        this.$refs.form.validate();
        if (this.$refs.wednesdayInput.valid === true) {
          this.$store.commit(
            "storeEditor/setWednesday",
            this.parseTimeStr(value)
          );
        }
      }
    },
    thursday: {
      get() {
        return this.parseTimes(
          this.$store.state.storeEditor.data.openingHours.thursday
        );
      },
      set(value) {
        this.$refs.form.validate();
        if (this.$refs.thursdayInput.valid === true) {
          this.$store.commit(
            "storeEditor/setThursday",
            this.parseTimeStr(value)
          );
        }
      }
    },
    friday: {
      get() {
        return this.parseTimes(
          this.$store.state.storeEditor.data.openingHours.friday
        );
      },
      set(value) {
        this.$refs.form.validate();
        if (this.$refs.fridayInput.valid === true) {
          this.$store.commit("storeEditor/setFriday", this.parseTimeStr(value));
        }
      }
    },
    saturday: {
      get() {
        return this.parseTimes(
          this.$store.state.storeEditor.data.openingHours.saturday
        );
      },
      set(value) {
        this.$refs.form.validate();
        if (this.$refs.saturdayInput.valid === true) {
          this.$store.commit(
            "storeEditor/setSaturday",
            this.parseTimeStr(value)
          );
        }
      }
    },
    sunday: {
      get() {
        return this.parseTimes(
          this.$store.state.storeEditor.data.openingHours.sunday
        );
      },
      set(value) {
        this.$refs.form.validate();
        if (this.$refs.sundayInput.valid === true) {
          this.$store.commit("storeEditor/setSunday", this.parseTimeStr(value));
        }
      }
    }
  },

  methods: {
    parseTimeStr: function(time) {
      if (time.trim().length === 0) return [];

      return time
        .trim()
        .split(",")
        .map(time => {
          let timeParts = time.split("-");
          let fromParts = timeParts[0].split(":");
          let toParts = timeParts[1].split(":");

          return {
            from: parseInt(fromParts[0]) * 60 + parseInt(fromParts[1]),
            to: parseInt(toParts[0]) * 60 + parseInt(toParts[1])
          };
        });
    },

    parseTimes: function(times) {
      if (!times || times.length === 0) return "";

      let openingTimeToString = time => {
        let hours = Math.floor(time / 60);
        let hoursStr = hours < 10 ? `0${hours}` : `${hours}`;

        let minutes = time % 60;
        let minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;

        return `${hoursStr}:${minutesStr}`;
      };

      return times
        .map(v => `${openingTimeToString(v.from)}-${openingTimeToString(v.to)}`)
        .join(",");
    }
  },

  created() {
    this.$store.dispatch("cities/fetchAll");
    this.$store.dispatch("categories/fetchAll");
    this.$store.dispatch("labels/fetchAll");
  }
};
</script>
